import { isServiceError, ServiceError, unsupported } from '../errors'
import { pmsUrl, postJson } from '../http'
import type { Dispositive, Stay } from '../reserva/types'
// Importación circular con mock.ts: solo se usa dentro de funciones, nunca al cargar el módulo.
import { climaMock } from './mock'
import type { Climate, ClimateMode, ClimateResult, ClimateZone, ClimaService } from './types'

/** `getdispositivestate_guestapp` (E16), copiado de src/api/gestdispositivestate_guestapp/ de la app actual. */
interface ApiDispositiveState {
  ok: boolean
  status?: string
  reserveDataGuestAppResponseList?: { roomData: { dispositiveList: { on: boolean; homeTemperature: string; airTemperature: string; airState: 'cool' | 'heat' | 'off'; minTemperature: string; maxTemperature: string }[] } }[]
}

const CLIMATE_TYPE = 'aire_acondicionado'
const MODE_BY_COMMAND: Record<string, ClimateMode> = { calor: 'heat', frio: 'cool', apagado: 'off', auto: 'auto' }
const COMMAND_BY_MODE: Record<ClimateMode, string> = { heat: 'calor', cool: 'frio', off: 'apagado', auto: 'auto' }
const CLIMATE_GUIDE_RE = /clima|aire|calefacci|termostat/i

const num = (s: string | undefined) => {
  const n = Number((s ?? '').replace(',', '.'))
  return Number.isFinite(n) && s !== undefined && s !== '' ? n : null
}

export const climateDevices = (stay: Pick<Stay, 'dispositives'>): Dispositive[] => stay.dispositives.filter((d) => d.type === CLIMATE_TYPE)

/** Parte del clima que se deriva de E1 sin llamar a E16 (variante, estado, modos, instrucciones). */
export function deriveClimate(stay: Pick<Stay, 'dispositives' | 'guide' | 'flags' | 'dates' | 'phase'>): Climate {
  const devices = climateDevices(stay)
  const instructions = stay.guide.manual.find((it) => CLIMATE_GUIDE_RE.test(it.name)) ?? null
  const empty = { presets: [], alerts: [], steps: [], houseRule: null }
  if (devices.length === 0) {
    return { available: instructions !== null, variant: 'static', state: 'unavailable', activeFrom: null, zones: [], range: null, modes: [], supportsArrivalPrep: false, instructions, ...empty }
  }
  const modes = [...new Set(devices.flatMap((d) => d.commands.map((c) => MODE_BY_COMMAND[c.type] ?? MODE_BY_COMMAND[c.name.toLowerCase()]).filter((m): m is ClimateMode => Boolean(m))))]
  const state: Climate['state'] = stay.phase === 'after' || stay.phase === 'cancelled' ? 'unavailable' : stay.flags.showDomotic ? 'active' : 'locked'
  return {
    available: true,
    variant: 'connected',
    state,
    activeFrom: stay.dates.checkIn,
    zones: devices.map((d) => ({ id: d.id, name: d.name, on: false, current: null, target: null, mode: 'off' })),
    range: null,
    modes,
    supportsArrivalPrep: false,
    instructions: null,
    ...empty,
  }
}

type StayArg = Pick<Stay, 'id' | 'guestCode' | 'serverPath' | 'dispositives'>

/** E16 de una zona. */
async function readZone(stay: StayArg, zone: ClimateZone, signal?: AbortSignal) {
  const data = await postJson<ApiDispositiveState>(pmsUrl(stay.serverPath, 'getdispositivestate_guestapp'), { reserveId: stay.id, dispositiveId: zone.id }, { auth: 'web', ...(signal ? { signal } : {}) })
  const st = data.reserveDataGuestAppResponseList?.[0]?.roomData.dispositiveList[0]
  if (!data.ok || !st) throw new ServiceError('server', data.status)
  return {
    zone: { ...zone, on: Boolean(st.on), current: num(st.homeTemperature), target: num(st.airTemperature), mode: st.airState ?? 'off' } satisfies ClimateZone,
    min: num(st.minTemperature),
    max: num(st.maxTemperature),
  }
}

/** Comando de la zona por tipo, como `commandList.find(c => c.type.toLowerCase() === …)` de la app actual. */
function command(stay: StayArg, zoneId: string, type: string) {
  return stay.dispositives.find((d) => d.id === zoneId)?.commands.find((c) => c.type.toLowerCase() === type) ?? null
}

/**
 * E17 `usedomoticcommand` para el clima (useAirConditioning.tsx `genPostData`): `serverPath` de la reserva,
 * sin auth, `{reserveId, userLang, commandId}` y `value` solo con la temperatura. A diferencia de la app
 * actual, `ok: false` cuenta como error (la app actual lo ignoraba y solo releía el estado).
 */
async function sendCommands(stay: StayArg, lang: string, commands: { id: string; value?: string }[]) {
  for (const c of commands) {
    const res = await postJson<{ ok: boolean }>(pmsUrl(stay.serverPath, 'usedomoticcommand'), { reserveId: stay.id, userLang: lang, commandId: c.id, ...(c.value ? { value: c.value } : {}) }, { auth: 'none' })
    if (!res.ok) throw new ServiceError('server', 'not_ok')
  }
}

async function run(stay: StayArg, zone: ClimateZone, lang: string, commands: ({ id: string; value?: string } | null)[]): Promise<ClimateResult> {
  if (commands.some((c) => c === null)) return { status: 'error', message: 'no_command' }
  try {
    await sendCommands(stay, lang, commands as { id: string; value?: string }[])
    const { zone: updated } = await readZone(stay, zone)
    return { status: 'applied', zone: updated }
  } catch (e) {
    if (isServiceError(e) && e.code === 'offline') throw e
    return { status: 'error', message: e instanceof Error ? e.message : String(e) }
  }
}

export const climaReal: ClimaService = {
  // "Prepara tu llegada" no existe en la API actual: delega en el mock (inventario D29, docs/backend/clima.md).
  getArrivalPrep: (stay, signal) => climaMock.getArrivalPrep(stay, signal),
  setArrivalPrep: (stay, target) => climaMock.setArrivalPrep(stay, target),
  async getClimate(stay, _lang, signal) {
    const base = deriveClimate(stay)
    if (base.variant !== 'connected' || base.state !== 'active') return base
    let range: Climate['range'] = null
    const zones: ClimateZone[] = []
    for (const zone of base.zones) {
      const read = await readZone(stay, zone, signal)
      zones.push(read.zone)
      // E17 `temperatura` recibe grados enteros (inventario D13).
      if (read.min !== null && read.max !== null && !range) range = { min: read.min, max: read.max, step: 1 }
    }
    return { ...base, zones, range }
  },

  setTarget(stay, zone, target, lang) {
    const modeCommand = command(stay, zone.id, zone.mode === 'heat' ? 'calor' : 'frio')
    const tempCommand = command(stay, zone.id, 'temperatura')
    return run(stay, zone, lang, [modeCommand, tempCommand && { id: tempCommand.id, value: String(target) }])
  },

  setMode(stay, zone, mode, lang) {
    return run(stay, zone, lang, [command(stay, zone.id, COMMAND_BY_MODE[mode])])
  },

  applyPreset() {
    return unsupported()
  },
}
